import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ProfileFormData {
  ranchName: string;
  location: string;
  description: string;
  locationCoordinates: [number, number] | null;
  profileImage: string;
  coverImage: string;
  breeds: string[];
}

export const useProfileData = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    ranchName: '',
    location: '',
    description: '',
    locationCoordinates: null,
    profileImage: '',
    coverImage: '',
    breeds: []
  });

  // Load profile data from database
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('business_name, location, description, profile_image_url, cover_image_url, breeds')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading profile:', error);
          return;
        }

        if (data) {
          // Safely handle the breeds field - ensure it's a string array
          const breedsArray = Array.isArray(data.breeds) && 
            data.breeds.every(breed => typeof breed === 'string') 
            ? data.breeds as string[] 
            : [];

          setFormData({
            ranchName: data.business_name || '',
            location: data.location || '',
            description: data.description || '',
            locationCoordinates: null,
            profileImage: data.profile_image_url || '',
            coverImage: data.cover_image_url || '',
            breeds: breedsArray
          });
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
      }
    };

    loadProfileData();
  }, [user]);

  const handleLocationChange = (location: string, coordinates?: [number, number]) => {
    setFormData(prev => ({
      ...prev,
      location,
      locationCoordinates: coordinates || null
    }));
    setHasChanges(true);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleBreedAdd = (breed: string) => {
    if (breed && !formData.breeds.includes(breed)) {
      setFormData(prev => ({
        ...prev,
        breeds: [...prev.breeds, breed]
      }));
      setHasChanges(true);
    }
  };

  const handleBreedRemove = (breed: string) => {
    setFormData(prev => ({
      ...prev,
      breeds: prev.breeds.filter(b => b !== breed)
    }));
    setHasChanges(true);
  };

  const handleImageUpdate = (type: 'profile' | 'cover', url: string) => {
    setFormData(prev => ({
      ...prev,
      [type === 'profile' ? 'profileImage' : 'coverImage']: url
    }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const updateData: any = {
        business_name: formData.ranchName,
        location: formData.location,
        description: formData.description,
        profile_image_url: formData.profileImage,
        cover_image_url: formData.coverImage,
        breeds: formData.breeds,
        updated_at: new Date().toISOString()
      };

      // If we have coordinates, store them as JSON
      if (formData.locationCoordinates) {
        updateData.location_coordinates = JSON.stringify({
          longitude: formData.locationCoordinates[0],
          latitude: formData.locationCoordinates[1]
        });
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      setHasChanges(false);
      toast({
        title: "Success",
        description: "Profile updated successfully.",
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    hasChanges,
    handleLocationChange,
    handleInputChange,
    handleBreedAdd,
    handleBreedRemove,
    handleImageUpdate,
    handleSaveChanges
  };
};
